import { TWidget } from 'widgets/Widget';

/**
 * @description Base RefinementsShowMore implementation
 * @param Widget Base widget for extending
 * @returns RefinementsShowMore class
 */
function RefinementsShowMoreClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class RefinementsShowMore
     * @augments RefinementsShowMore
     * @property {string} data-widget - Widget name `RefinementsShowMore`
     */
    class RefinementsShowMore extends Widget {
        hiddenItems: NodeListOf<Element> | undefined;

        isItemsHidden = true;

        isRefreshAction = false;

        prefs() {
            return {
                hiddenCssClass: 'm-hidden',
                itemCssClass: '.js-refinement_toggle',
                itemsListCssClass: '.b-refinement',
                focusableItemCssClass: '.b-refinement_swatch',
                tablistItemCssClass: 'b-refinement_tablist-swatch',
                ...super.prefs()
            };
        }

        getHiddenItems() {
            this.hiddenItems =
                this.ref('self').get()?.closest(this.prefs().itemsListCssClass)?.querySelectorAll(this.prefs().itemCssClass) || undefined;
        }

        handleShowMore() {
            this.isItemsHidden = false;
            this.isRefreshAction = false;
            this.toggleMoreItems();
        }

        handleShowLess() {
            this.isItemsHidden = true;
            this.isRefreshAction = false;
            this.toggleMoreItems();
        }

        toggleMoreItems() {
            this.getHiddenItems();

            this.ref('showMore').attr('hidden', !this.isItemsHidden);
            this.ref('showLess').attr('hidden', this.isItemsHidden);

            if (!this.isRefreshAction) {
                this.ref(this.isItemsHidden ? 'showMore' : 'showLess').focus();
            }

            if (this.hiddenItems?.length) {
                this.hiddenItems.forEach((item, index) => {
                    item.classList.toggle(this.prefs().hiddenCssClass, this.isItemsHidden);

                    const focusableItem = item.querySelector(this.prefs().focusableItemCssClass);
                    const isTablistItem = focusableItem?.classList.contains(this.prefs().tablistItemCssClass);

                    focusableItem?.setAttribute(
                        'tabindex',
                        this.isItemsHidden || (this.isRefreshAction && isTablistItem && index !== 0) ? '-1' : '0'
                    );
                    focusableItem?.classList.toggle('js-hidden-refinement-swatch', this.isItemsHidden);

                    if (focusableItem && !this.isItemsHidden && index === 0) {
                        (focusableItem as HTMLElement).focus();
                    }
                });
            }

            this.eventBus().emit('update.accordion.item.height');
        }

        /**
         * @description On Refresh Handler
         */
        onRefresh() {
            super.onRefresh();
            this.isRefreshAction = true;

            this.toggleMoreItems();
        }
    }

    return RefinementsShowMore;
}

export type TRefinementsShowMore = ReturnType<typeof RefinementsShowMoreClassCreator>;

export type TRefinementsShowMoreInstance = InstanceType<TRefinementsShowMore>;

export default RefinementsShowMoreClassCreator;
